import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Container,
  EmptyContent,
  Rating,
  Separator,
  Text,
} from "rothzerg-ui";
import { BreadcrumbNav } from "@/components/catalog/BreadcrumbNav";
import { FeedbackBanner } from "@/components/catalog/FeedbackBanner";
import { ProductCarousel } from "@/components/catalog/ProductCarousel";
import { getProductBySlug, getRelatedProducts } from "@/data/selectors";
import { categoryPath, homePath } from "@/lib/routes";

function IconHeart() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function IconShare() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconTruck() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 5v3h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}
function IconStore() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export function ProductPage() {
  const { productSlug = "" } = useParams();
  const product = getProductBySlug(productSlug);
  const [activeImage, setActiveImage] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);
  const [addedToBag, setAddedToBag] = useState(false);

  if (!product) {
    return (
      <Container>
        <div style={{ padding: "4rem 0" }}>
          <EmptyContent
            title="Product not found"
            description="This product may no longer be available."
            actions={<Link to={homePath()}>Back to home</Link>}
          />
        </div>
      </Container>
    );
  }

  const images = product.images ?? [product.image];
  const related = getRelatedProducts(product.id, 10);
  const brandRelated = getRelatedProducts(product.id, 10).filter(
    (p) => p.brandSlug === product.brandSlug,
  );
  const savings =
    product.wasPrice && product.wasPrice > product.price
      ? product.wasPrice - product.price
      : null;

  const breadcrumbs = [
    { label: "Home", href: homePath() },
    {
      label: product.categorySlug.replace(/-/g, " "),
      href: categoryPath(product.categorySlug),
    },
    { label: product.title },
  ];

  function handleAddToBag() {
    if (!product?.inStock) return;
    setAddedToBag(true);
    setTimeout(() => setAddedToBag(false), 2000);
  }

  return (
    <div id="product-page" className="kmart-page">
      <Container>
        <div className="pdp-breadcrumb">
          <BreadcrumbNav items={breadcrumbs} />
        </div>

        {/* ── Main 2-column layout ── */}
        <div className="pdp-layout">
          {/* LEFT: Gallery */}
          <div id="product-gallery" className="pdp-gallery">
            {/* Thumbnail strip */}
            <div className="pdp-gallery__thumbs" role="list" aria-label="Product images">
              {images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  role="listitem"
                  aria-label={`View image ${i + 1}`}
                  aria-pressed={i === activeImage}
                  className={`pdp-gallery__thumb${i === activeImage ? " pdp-gallery__thumb--active" : ""}`}
                  onClick={() => setActiveImage(i)}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>

            {/* Main image */}
            <div className="pdp-gallery__main-wrap">
              {product.badges?.[0] && (
                <span className="pdp-gallery__badge">
                  {product.badges[0].label}
                </span>
              )}
              <img
                src={images[activeImage]}
                alt={product.title}
                className="pdp-gallery__main"
              />
            </div>
          </div>

          {/* RIGHT: Product info */}
          <div className="pdp-info">
            {/* Seller + share row */}
            <div className="pdp-info__top-row">
              <span className="pdp-info__seller">
                {product.sellerLabel ?? (product.seller === "kmart" ? "Kmart" : "Marketplace")}
              </span>
              <button type="button" className="pdp-share-btn" aria-label="Share product">
                <IconShare /> Share
              </button>
            </div>

            {/* Title */}
            <Text variant="h2" as="h1" className="pdp-info__title">
              {product.title}
            </Text>

            {/* Rating row */}
            {product.rating != null && (
              <div className="pdp-info__rating-row">
                <Rating value={product.rating} readOnly size="sm" />
                <span className="pdp-info__review-count">
                  ({product.reviewCount ?? 0} reviews)
                </span>
              </div>
            )}

            {/* Price block */}
            <div className="pdp-info__price-block">
              <span className="pdp-info__price">${product.price}</span>
              {product.wasPrice && product.wasPrice > product.price && (
                <>
                  <span className="pdp-info__was">${product.wasPrice}</span>
                  <span className="pdp-info__save-badge">Save ${savings}</span>
                </>
              )}
            </div>

            {/* Stock status */}
            {product.inStock ? (
              <p className="pdp-info__stock pdp-info__stock--in">
                <IconCheck /> In stock
              </p>
            ) : (
              <p className="pdp-info__stock pdp-info__stock--out">
                Out of stock
              </p>
            )}

            {/* Colour swatches */}
            {product.colors && product.colors.length > 0 && (
              <div className="pdp-info__colours">
                <span className="pdp-info__colours-label">Colour:</span>
                {product.colors.map((c) => (
                  <span
                    key={c.name}
                    title={c.name}
                    className="pdp-colour-swatch"
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
            )}

            {/* CTA row */}
            <div className="pdp-info__cta">
              <Button
                id="add-to-bag"
                fullWidth
                size="lg"
                disabled={!product.inStock}
                onClick={handleAddToBag}
                className={addedToBag ? "pdp-btn--added" : ""}
              >
                {addedToBag ? "✓ Added to Bag" : "Add to Bag"}
              </Button>
              <button
                type="button"
                id="add-to-wishlist"
                aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                aria-pressed={wishlisted}
                className={`pdp-wishlist-btn${wishlisted ? " pdp-wishlist-btn--active" : ""}`}
                onClick={() => setWishlisted((v) => !v)}
              >
                <IconHeart />
              </button>
            </div>

            {/* BNPL */}
            <div className="pdp-bnpl">
              <span className="pdp-bnpl__label">Buy now, pay later with</span>
              <div className="pdp-bnpl__logos">
                {["Afterpay", "Zip", "PayPal"].map((name) => (
                  <span key={name} className="pdp-bnpl__pill">{name}</span>
                ))}
              </div>
            </div>

            {/* Fulfillment cards */}
            <div id="fulfillment-info" className="pdp-fulfillment">
              <div className="pdp-fulfillment__card">
                <IconTruck />
                <div>
                  <strong>Delivery</strong>
                  <p>Free on orders over $65</p>
                </div>
              </div>
              <div className="pdp-fulfillment__card">
                <IconStore />
                <div>
                  <strong>Click & Collect</strong>
                  <p>Ready in 4 hours — check stores</p>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="pdp-trust">
              {["60-day returns", "Secure checkout", "Genuine products"].map((t) => (
                <span key={t} className="pdp-trust__item">
                  <IconCheck /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Product details below the fold ── */}
        <div id="product-details" className="pdp-details">
          {product.description && (
            <div className="pdp-details__section">
              <Text variant="h3">Description</Text>
              <Text variant="body" style={{ marginTop: "0.5rem", lineHeight: 1.7 }}>
                {product.description}
              </Text>
            </div>
          )}

          {(product.details || product.features) && (
            <div className="pdp-details__cols">
              {product.details && (
                <div>
                  <Text variant="h4">Product details</Text>
                  <ul className="pdp-details__list">
                    {product.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
              {product.features && (
                <div>
                  <Text variant="h4">Features</Text>
                  <ul className="pdp-details__list">
                    {product.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div id="product-accordion" className="pdp-accordion">
            <Accordion type="multiple">
              <AccordionItem value="delivery">
                <AccordionTrigger>Delivery & Returns</AccordionTrigger>
                <AccordionContent>
                  <p>Free standard delivery on orders over $65 — typically 3–5 business days.</p>
                  <p style={{ marginTop: "0.5rem" }}>60-day change of mind returns on eligible items. Items must be unused and in original packaging.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="warranty">
                <AccordionTrigger>Warranty & Support</AccordionTrigger>
                <AccordionContent>
                  <p>This product comes with guarantees that cannot be excluded under the Australian Consumer Law.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="safety">
                <AccordionTrigger>Safety Information</AccordionTrigger>
                <AccordionContent>
                  <p>Keep out of reach of children. See product packaging for full safety details.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </Container>

      {/* ── Recommendation carousels ── */}
      <div className="pdp-carousels">
        <ProductCarousel id="carousel-also-like" title="You may also like" products={related} />
        {(brandRelated.length > 0) && (
          <ProductCarousel id="carousel-brand" title="More from this brand" products={brandRelated} />
        )}
        <ProductCarousel id="carousel-recommended" title="Recommended for you" products={related} />
        <ProductCarousel id="carousel-explore" title="More items to explore" products={related} />
      </div>

      <FeedbackBanner />
    </div>
  );
}
